<template>
  <div>
    <v-row justify="space-between">
      <v-col cols="12" xl="3" lg="3" md="3" sm="4" xs="12">
        <SearchText
          :text="'Search by name'"
          :value="'text'"
          @searchTextChanged="searchTextChanged"
          @keypress.enter.native.prevent="triggerSearch"
          @triggerSearch="triggerSearch"
          @triggerClear="triggerClear"
        ></SearchText>
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <v-btn
          v-if="$store.getters.roles == 'super-admin'"
          @click="downloadServices"
          outlined
          class="black--text font-weight-bold mb-2"
          rounded
          large
        >
          <v-icon color="black" small class="mr-2">
            $vuetify.icons.plus
          </v-icon>
          Download
        </v-btn>
        <v-btn
          @click="triggerBulkEdit"
          outlined
          class="white--text font-weight-bold mb-2 black ml-2"
          rounded
          large
        >
          Bulk Edit
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :items="items"
      class="mt-5"
      :options.sync="options"
      :server-items-length="totalItems"
      :loading="loading"
      :multi-sort="false"
      :footer-props="footerProps"
      item-class="properties"
      :dense="false"
      :headers="headers"
      id="table"
      :single-select="false"
      v-model="selected"
      show-select
    >
      <template v-slot:item.logo="{ item }" style="width: 7em !important;">
        <v-img
          class="my-2"
          style="border-radius: 8px;"
          height="75"
          width="65"
          :src="item.logoUrlPublic"
          contain
        >
          <template v-slot:placeholder>
            <v-img style="border-radius: 8px;" lazy-src="@/assets/img/blur.jpg"></v-img>
          </template>
        </v-img>
      </template>
      <template v-slot:item.name="{ item }">
        <div class="font-weight-bold">
          {{ item.name }}
        </div>
        <div v-if="item.draft">Status: <span class="text-bold">Draft</span></div>
        <div v-else-if="item.published">Status: <span class="text-bold">Public</span></div>
        <div v-else-if="!item.published">Status: <span class="text-bold">Private</span></div>
        <div>
          Last updated:
          <span class="text-bold">{{ formatDateTimeLocale(item.updatedTimestamp) }}</span>
        </div>
      </template>
      <template v-slot:item.createdTimestamp="{ item }">
        <span>
          {{ formatDateTimeLocale(item.createdTimestamp) }}
        </span>
      </template>
      <template v-slot:item.provider="{ item }">
        <router-link
          :to="{ name: 'OverviewProvider', params: { id: item.provider.id } }"
          style="text-decoration: underline; color: inherit;"
        >
          {{ item.provider.name }}
        </router-link>
      </template>
      <template v-slot:item.hosting="{ item }">
        <router-link
          :to="{
            name: 'OverviewHostingInstitution',
            params: { id: item.provider.hostingInstitution.id },
          }"
          style="text-decoration: underline; color: inherit;"
        >
          {{ item.provider.hostingInstitution.name }}
        </router-link>
      </template>
      <template v-slot:item.type="{ item }">
        <div>
          <div v-for="(type, index) in item.types.slice(0, 2)" :key="index">
            <v-chip class="my-1" :style="{ backgroundColor: 'rgba(237, 224, 255, 1)' }">
              {{ type }}
            </v-chip>
          </div>
          <v-chip v-if="item.types.length > 2" class="my-1" color="primary200">
            +{{ item.types.length - 2 }} more
          </v-chip>
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-layout>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="onEditItem(item)"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-pencil-outline
                </v-icon>
              </v-btn>
            </template>
            {{ $t('edit') }}
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                :to="{
                  name: 'OverviewService',
                  params: { id: item.id },
                  query: {
                    fromTable: true,
                    ...getParams(),
                  },
                }"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-eye
                </v-icon>
              </v-btn>
            </template>
            {{ $t('Overview') }}
          </v-tooltip>
          <v-tooltip bottom v-if="$store.getters.roles == 'provider'">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="duplicateResource(item)"
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-content-copy
                </v-icon>
              </v-btn>
            </template>
            {{ $t('Copy') }}
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="(itemToBeDeleted = item), (deleteDialog = true)"
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-delete
                </v-icon>
              </v-btn>
            </template>
            {{ $t('Delete') }}
          </v-tooltip>
        </v-layout>
      </template>
    </v-data-table>

    <!-- Delete Dialog -->
    <v-dialog v-if="itemToBeDeleted !== null" v-model="deleteDialog" max-width="1000px">
      <v-card class="py-4 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title class="px-0 py-0">
          <v-row class="mx-0 px-2" align="center">
            <v-col cols="11" class="justify-center d-flex">
              <img
                alt="Logo"
                width="180"
                class="rounded-0 text-center"
                :src="require('@/assets/svg/logo.svg')"
              />
            </v-col>
            <v-col cols="1" class="pl-0">
              <v-btn fab small text @click="deleteDialog = false">
                <v-icon color="neutral500">
                  mdi-close
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider class="custom-divider mt-10"></v-divider>

        <v-card-text class="justify-center d-flex mt-2">
          <v-icon size="150" color="black">$vuetify.icons.errorCirle</v-icon>
        </v-card-text>
        <v-card-text
          class="justify-center d-flex mt-2 black--text font-weight-bold"
          style="font-size: 20px;"
        >
          <div>Delete this Service?</div>
        </v-card-text>
        <v-card-text class="justify-center d-flex mt-2">
          <div>
            You will not be able to recover it.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-row style="width: 100%">
            <v-col cols="6">
              <v-btn
                @click="deleteDialog = false"
                block
                depressed
                rounded
                large
                class="neutral800--text font-weight-bold"
                color="rgba(157, 87, 244, 1)"
                outlined
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                @click="onDeleteItem"
                :disabled="disableDelete"
                block
                depressed
                rounded
                large
                class="white--text font-weight-bold"
                color="rgba(157, 87, 244, 1)"
                >{{ $t('Delete') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Copy Dialog -->
    <v-dialog v-if="duplicateItem" v-model="openDuplicateResource" max-width="675px">
      <v-card class="py-4 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title class="px-0 py-0">
          <v-row class="mx-0 px-2" align="center">
            <v-col cols="11" class="text-center d-flex justify-center align-center">
              <img id="logo-image" :src="require('@/assets/logo-purple.svg')" />
            </v-col>
            <v-col cols="1" class="pl-0">
              <v-btn fab small text @click="openDuplicateResource = false">
                <v-icon color="neutral500">
                  mdi-close
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider class="my-1 my-sm-6 my-lg-6 my-xl-6 my-md-6"></v-divider>
        <v-card-text class="text-center">
          <img id="logo-image" :src="require('@/assets/svg/copy.svg')" />
          <h3 class="pb-2">Copied {{ duplicateItem.name }}</h3>
          <p>
            Please click on the button to access the new version and make any necessary changes.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-row style="width: 100%" class="text-center">
            <v-col cols="12">
              <v-btn
                v-if="newDuplicateItem"
                depressed
                rounded
                large
                class="white--text font-weight-bold"
                color="black"
                :to="{ name: 'OverviewService', params: { id: newDuplicateItem.id } }"
                >Open copied service
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Edit Dialog -->
    <v-dialog v-model="dialogBulkEdit" max-width="500" persistent>
      <v-card>
        <v-card-title class="pb-5">
          <v-row>
            <v-col cols="12" class="justify-start d-flex">
              Bulk Edit
            </v-col>
          </v-row>
          <v-btn icon @click="dialogBulkEdit = false" left>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="mb-0 pb-0">
          <label>Service to copy from</label>
          <p class="label-info">
            Select the Service you want to use as protorype
          </p>
          <input-autocomplete-search
            v-model="bulkEdit.servicePrototype"
            :item-value="'id'"
            :item-text="'name'"
            :url="'/api/v1/resources/by-name?name='"
            label="Service to copy from"
            prepend-inner-icon="mdi-magnify"
            clearable
            return-object
            outlined
          ></input-autocomplete-search>
        </v-card-text>
        <v-card-text>
          <v-checkbox v-model="bulkEdit.logo" label="Same Logo" class="mt-0 pt-0" />
        </v-card-text>
        <v-card-text>
          <v-checkbox
            v-model="bulkEdit.publicContact"
            label="Same Public Contact"
            class="mt-0 pt-0"
          />
        </v-card-text>
        <v-card-actions class="pb-5">
          <v-row>
            <v-col cols="6">
              <v-btn
                @click="cancelBulkEdit"
                block
                large
                class="rounded-lg black--text  font-weight-bold"
                outlined
                color="black"
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                @click="submitBulkEdit"
                large
                class="rounded-lg white--text  font-weight-bold"
                color="neutral"
                block
                >{{ $t('submit') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import InputAutocompleteSearch from '@/components/shared/inputs/InputAutocompleteSearch.vue';
import SearchText from '@/components/shared/search/SearchText';

export default {
  name: 'TableResources',
  components: { InputAutocompleteSearch, SearchText },
  mixins: [util],
  props: {
    urlApi: String,
    /*
    if you do not want to use generic urlApi, use urlApiDelete
     */
    urlApiDelete: String,
    urlApiCopy: String,
    search: Object,
    searchAttributes: Array,
    editRouter: String,
    viewRouter: String,
  },
  data: () => ({
    firstLoad: true,
    totalItems: 0,
    items: [],
    loading: true,
    options: {},
    itemToBeDeleted: null,
    deleteDialog: false,
    openDuplicateResource: false,
    duplicateItem: null,
    newDuplicateItem: null,
    disableDelete: false,
    headers: [
      { text: '', value: 'actions', sortable: false },
      { text: 'Logo', value: 'logo', sortable: false },
      { text: 'Details', value: 'name', sortable: true },
      { text: 'Type', value: 'type', sortable: false },
      { text: 'Created', value: 'createdTimestamp', sortable: true },
      { text: 'Provider', value: 'provider', sortable: true },
      { text: 'Hosting', value: 'hosting', sortable: true },
    ],
    footerProps: { 'items-per-page-options': [10, 15, 25, 50, 100] },
    selected: [],
    dialogBulkEdit: false,
    bulkEdit: {
      services: [],
      servicePrototype: null,
      logo: false,
      publicContact: false,
    },
  }),
  watch: {
    options: {
      handler() {
        console.log('[Table] Options watch');
        if (!this.firstLoad) {
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
        }
      },
      deep: true,
    },
  },
  mounted() {
    console.log('[Table] Mounted');
    if (this.firstLoad) {
      /* Populate table from route */
      this.options.page = this.$route.query.index ? Number(this.$route.query.index) + 1 : 1;
      this.options.itemsPerPage = this.$route.query.size ? Number(this.$route.query.size) : 10;
      this.options.sortBy = [];
      this.options.sortDesc = [];
      if (this.$route.query.sort) {
        const sortList = this.$route.query.sort.split(',');
        for (let i = 0; i < sortList.length; i += 1) {
          this.options.sortBy.push(sortList[i].split(' ')[0]);
          if (sortList[i].split(' ')[1] === 'asc') {
            this.options.sortDesc.push(false);
          } else {
            this.options.sortDesc.push(true);
          }
        }
      }
      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.$route.query[attr]) {
            this.$set(this.search, attr, this.$route.query[attr]);
          }
        }
      }
    }
    this.getDataFromApi().then(data => {
      this.items = data.items;
      this.totalItems = data.total;
      this.firstLoad = false;
    });
  },
  methods: {
    searchTextChanged(obj) {
      console.log('[Table] searchTextChanged');
      this.search[obj.value] = obj.search;
      console.log(this.search);
    },
    triggerSearch() {
      console.log('[Table] Search watch');
      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    triggerClear() {
      console.log('[Table] Clear watch');

      this.search = {};

      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    getDataFromApi() {
      console.log('[Table] Loading data');
      this.loading = true;
      EventBus.$emit('waiting', true);
      return new Promise(resolve => {
        this.axios
          .get(`${this.urlApi}?providerId=${sessionStorage.getItem('providerId')}`, {
            params: {
              ...this.getParams(),
              isEquipment: false,
            },
          })
          .then(res => {
            let items = [];
            let total = 0;
            if (res.status === 200) {
              items = res.data.content;
              total = res.data.total;
            }
            setTimeout(() => {
              this.loading = false;
              resolve({
                items,
                total,
              });
            });
          })
          .catch(error => {
            console.error('[Table] Error loading data:', error);
            this.loading = false;
            resolve({
              items: [],
              total: 0,
            });
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      });
    },
    getParams() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      const query = {
        index: page - 1,
        size: itemsPerPage,
      };
      let sort = '';
      for (let i = 0; i < sortBy.length; i += 1) {
        if (i !== 0) {
          sort += ',';
        }
        let dir = 'asc';
        if (sortDesc[i]) {
          dir = 'desc';
        }
        sort += `${sortBy[i]} ${dir}`;
      }
      if (sort !== '') {
        query.sort = sort;
      }
      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.search[attr]) {
            query[attr] = this.search[attr];
          }
        }
      }
      return query;
    },
    onEditItem(value) {
      console.log('Click Edit ' + value.id);
      this.$router.push({
        name: this.editRouter,
        query: {
          id: value.id,
          fromTable: true,
          ...this.getParams(),
        },
      });
    },
    onDeleteItem() {
      this.disableDelete = true;
      let value = this.itemToBeDeleted;
      console.log('Click Delete ' + value.id);
      EventBus.$emit('waiting', true);
      this.axios
        .delete((this.urlApiDelete ? this.urlApiDelete : this.urlApi) + '/' + value.id)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('delete.success'),
          });
          this.deleteDialog = false;
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
          this.disableDelete = false;
        })
        .catch(error => {
          console.log(error);
          this.disableDelete = false;
        })
        .finally(EventBus.$emit('waiting', false));
    },
    duplicateResource(item) {
      this.duplicateItem = item;
      EventBus.$emit('waiting', true);
      this.axios
        .post(`${this.urlApiCopy}/${item.id}/copy`)
        .then(copy => {
          this.openDuplicateResource = true;
          this.newDuplicateItem = copy.data;
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
        });
    },
    triggerBulkEdit() {
      this.dialogBulkEdit = true;
    },
    cancelBulkEdit() {
      this.bulkEdit.servicePrototype = null;
      this.bulkEdit.logo = false;
      this.bulkEdit.publicContact = false;
      this.dialogBulkEdit = false;
    },
    submitBulkEdit() {
      EventBus.$emit('waiting', true);
      this.bulkEdit.services = this.selected;
      this.axios
        .post('/api/v1/resources/bulk', this.bulkEdit)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('resources-updated'),
          });
          this.dialogBulkEdit = false;
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    downloadServices() {
      this.axios
        .get('api/v1/resources/admin/download', {
          responseType: 'blob',
        })
        .then(response => {
          const contentDisposition = response.headers['content-disposition'];
          let fileName = 'services.xlsx';
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="([^"]*)"/);
            if (fileNameMatch) {
              fileName = fileNameMatch[1];
            }
          }

          const url = window.URL.createObjectURL(new Blob([response.data]));

          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);

          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(error => {
          console.error('Error downloading file:', error);
        });
    },
  },
};
</script>

<style>
.v-application--is-ltr .v-data-table__mobile-row__cell {
  text-align: left;
}

tr {
  min-height: 200px;
  height: 100%;
}

.custom-divider {
  padding-top: 5px;
  margin: 0 35px;
  border-top-width: 2px;
  border-top-style: solid;
  border-top-color: rgba(195, 194, 199, 1);
}
</style>
